import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Maximize2 } from "lucide-react";
import { useList } from "@/hooks/useApi";
import { useRealtime } from "@/context/RealtimeContext";
import { fmt } from "@/components/common";

const DEPTS = ["Cutting", "Printing", "Stitching", "Finishing", "Quality Control", "Packing"];

function Hero({ label, value, suffix, tone }) {
  const c = { success: "text-emerald-400", warning: "text-amber-400", danger: "text-red-400", primary: "text-sky-400" }[tone] || "text-white";
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl bg-slate-900/70 border border-slate-800 p-6">
      <div className="text-xs sm:text-sm uppercase tracking-[0.2em] text-slate-400 font-semibold mb-2 text-center">{label}</div>
      <div className={`font-num font-black text-4xl sm:text-6xl tracking-tighter ${c}`}>{value}{suffix}</div>
    </div>
  );
}

function DeptCard({ dept }) {
  const { data } = useList(`dept-${dept}`, `/departments/${encodeURIComponent(dept)}/dashboard`);
  const eff = data?.efficiency || 0;
  const color = eff >= 90 ? "text-emerald-400" : eff >= 60 ? "text-sky-400" : "text-amber-400";
  return (
    <div className="rounded-xl bg-slate-900/70 border border-slate-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-display font-bold uppercase tracking-wide text-lg text-white">{dept}</span>
        <span className={`font-num font-bold text-xl ${color}`}>{fmt(eff)}%</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div><div className="font-num font-bold text-2xl text-emerald-400">{fmt(data?.todays_production)}</div><div className="text-[10px] uppercase text-slate-500 tracking-wider">Today</div></div>
        <div><div className="font-num font-bold text-2xl text-sky-400">{fmt(data?.todays_target)}</div><div className="text-[10px] uppercase text-slate-500 tracking-wider">Target</div></div>
        <div><div className="font-num font-bold text-2xl text-red-400">{fmt(data?.rejected_quantity)}</div><div className="text-[10px] uppercase text-slate-500 tracking-wider">Reject</div></div>
      </div>
    </div>
  );
}

export default function LiveBoard() {
  const navigate = useNavigate();
  const { connected } = useRealtime();
  const { data } = useList("dashboard", "/dashboard");
  const [now, setNow] = useState(new Date());
  const k = data?.kpis || {};

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const goFull = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-5 sm:p-8" data-testid="live-board">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className={`live-dot w-3 h-3 rounded-full ${connected ? "bg-emerald-500" : "bg-slate-600"}`} />
            <span className={`text-sm font-bold tracking-widest ${connected ? "text-emerald-400" : "text-slate-500"}`}>{connected ? "LIVE" : "OFFLINE"}</span>
          </div>
          <h1 className="font-display font-extrabold text-2xl sm:text-4xl uppercase tracking-tight">Factory Live Board</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="font-num text-lg sm:text-2xl text-slate-300">{now.toLocaleTimeString()}</div>
          <button onClick={goFull} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" data-testid="fullscreen-btn"><Maximize2 className="w-5 h-5" /></button>
          <button onClick={() => navigate("/")} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" data-testid="board-close-btn"><X className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4">
        <Hero label="Today's Target" value={fmt(k.todays_target)} tone="primary" />
        <Hero label="Today's Production" value={fmt(k.todays_production)} tone="success" />
        <Hero label="Efficiency" value={fmt(k.production_efficiency)} suffix="%" tone="success" />
        <Hero label="Active Orders" value={fmt(k.total_active_orders)} />
        <Hero label="Completed" value={fmt(k.orders_completed)} tone="success" />
        <Hero label="Rejections" value={fmt(k.rejection_quantity)} tone="danger" />
        <Hero label="Machines Running" value={fmt(k.machines_running)} tone="success" />
        <Hero label="Delayed Jobs" value={fmt(k.delayed_orders)} tone="danger" />
      </div>

      <h2 className="font-display font-bold uppercase tracking-wide text-xl text-slate-400 mb-3">Department Status</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {DEPTS.map((d) => <DeptCard key={d} dept={d} />)}
      </div>
    </div>
  );
}
